import { TBC_TYPES, type TbcType } from './tbc-types';

/** Per-stitch-type default sizing. Mirrors how a Tbc stores sizing so the
 *  resolution tier needs no translation: `params` uses the same key grammar
 *  ('top-cap', 'crossbar', 'crossbarAngle#<barId>', 'bodyGapTop',
 *  'followTangent' as 0|1, ...) and `widthScale` covers the shared width
 *  field (sc-family width knob, rect width, cluster width). Sparse: only
 *  deviations from factory defaults are stored. */
export type StitchSizingDefaults = {
    params?: Record<string, number>;
    widthScale?: number;
};

/** User defaults for every stitch type, snapshotted into new documents as
 *  `editor.sizingDefaults`. Absent map / absent type / absent key ⇒ factory
 *  behavior, so pre-existing documents need no migration. */
export type CrochetSizingDefaults = Partial<Record<TbcType, StitchSizingDefaults>>;

export const SIZING_DEFAULTS_VERSION = 1;

export function docDefaultParam(
    dd: CrochetSizingDefaults | undefined, type: TbcType, key: string,
): number | undefined {
    return dd?.[type]?.params?.[key];
}

export function docDefaultWidthScale(
    dd: CrochetSizingDefaults | undefined, type: TbcType,
): number | undefined {
    return dd?.[type]?.widthScale;
}

export function isEmptySizingDefaults(dd: CrochetSizingDefaults | undefined): boolean {
    if (dd == null) return true;
    for (const entry of Object.values(dd)) {
        if (entry == null) continue;
        if (entry.widthScale != null) return false;
        if (entry.params != null && Object.keys(entry.params).length > 0) return false;
    }
    return true;
}

const TBC_TYPE_SET: ReadonlySet<string> = new Set(TBC_TYPES);

/** Shape-validate an untrusted value (server response, localStorage, stored
 *  document) into a CrochetSizingDefaults: unknown stitch types, non-finite
 *  numbers, non-positive widthScale, and empty entries are dropped. Range
 *  clamping happens at resolution time (each tier clamps to its own bounds). */
export function sanitizeCrochetSizingDefaults(raw: unknown): CrochetSizingDefaults {
    if (typeof raw !== 'object' || raw == null) return {};
    const out: CrochetSizingDefaults = {};
    for (const [type, entryRaw] of Object.entries(raw as Record<string, unknown>)) {
        if (!TBC_TYPE_SET.has(type)) continue;
        if (typeof entryRaw !== 'object' || entryRaw == null) continue;
        const e = entryRaw as Record<string, unknown>;
        const entry: StitchSizingDefaults = {};
        if (typeof e.widthScale === 'number' && Number.isFinite(e.widthScale) && e.widthScale > 0) {
            entry.widthScale = e.widthScale;
        }
        if (typeof e.params === 'object' && e.params != null) {
            const params: Record<string, number> = {};
            for (const [k, v] of Object.entries(e.params as Record<string, unknown>)) {
                if (typeof v === 'number' && Number.isFinite(v)) params[k] = v;
            }
            if (Object.keys(params).length > 0) entry.params = params;
        }
        if (entry.widthScale != null || entry.params != null) out[type as TbcType] = entry;
    }
    return out;
}

/** Deep, key-order-insensitive equality — used for settings dirty checks. */
export function sizingDefaultsEqual(a: CrochetSizingDefaults, b: CrochetSizingDefaults): boolean {
    return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

function normalize(dd: CrochetSizingDefaults): unknown {
    const types = Object.keys(dd).sort();
    return types.map((t) => {
        const e = dd[t as TbcType]!;
        const params = e.params == null
            ? null
            : Object.keys(e.params).sort().map((k) => [k, e.params![k]]);
        return [t, e.widthScale ?? null, params];
    });
}
